import { doc, getDoc, updateDoc, setDoc, collection, getDocs, query, where, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Get user's automation data for a specific automation
 * @param {string} userId - The user's UID
 * @param {string} automationId - The automation ID
 * @returns {Promise<Object>} User's automation data
 */
export const getUserAutomationData = async (userId, automationId, accountId = null) => {
  try {
    // Prefer company-wide automation state when an account/company ID is available
    if (accountId) {
      const accountRef = doc(db, 'accounts', accountId);
      const accountDoc = await getDoc(accountRef);
      if (accountDoc.exists()) {
        const accountData = accountDoc.data();
        const accountAutomations = accountData.userAutomations || {};
        if (accountAutomations[automationId]) {
          return accountAutomations[automationId];
        }
      }
    }

    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const userAutomations = userData.userAutomations || {};
      if (userAutomations[automationId]) {
        return userAutomations[automationId];
      }
    }

    return {
      status: 'off',
      customizations: {},
      lastUpdated: null
    };
  } catch (error) {
    console.error('Error fetching user automation data:', error);
    return {
      status: 'off',
      customizations: {},
      lastUpdated: null
    };
  }
};

/**
 * Save user's automation status
 * @param {string} userId - The user's UID
 * @param {string} automationId - The automation ID
 * @param {string} status - The automation status ('off', 'requested', 'in-progress', 'on')
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveUserAutomationStatus = async (userId, automationId, status, accountId = null) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    // Get user data for company/account info
    let userData = {};
    let company = 'Unknown Company';
    
    if (userDoc.exists()) {
      userData = userDoc.data();
      company = userData.company || userData.businessName || 'Unknown Company';
    }
    
    // Get automation template data
    const automationDoc = await getDoc(doc(db, 'automations', automationId));
    let automationData = {};
    
    if (automationDoc.exists()) {
      automationData = automationDoc.data();
    }
    
    // Update user document
    if (userDoc.exists()) {
      const userAutomations = userData.userAutomations || {};
      
      userAutomations[automationId] = {
        ...userAutomations[automationId],
        status,
        lastUpdated: serverTimestamp()
      };
      
      await updateDoc(userDocRef, {
        userAutomations,
        lastUpdated: serverTimestamp()
      });
    } else {
      // Create user document if it doesn't exist
      await setDoc(userDocRef, {
        uid: userId,
        userAutomations: {
          [automationId]: {
            status,
            customizations: {},
            lastUpdated: serverTimestamp()
          }
        },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    }
    
    // Also persist company-wide status when accountId is provided so all team members share it
    if (accountId) {
      try {
        const accountRef = doc(db, 'accounts', accountId);
        const accountDoc = await getDoc(accountRef);
        const accountData = accountDoc.exists() ? accountDoc.data() : {};
        const accountAutomations = accountData.userAutomations || {};
        accountAutomations[automationId] = {
          ...(accountAutomations[automationId] || {}),
          status,
          lastUpdated: serverTimestamp()
        };
        if (Object.keys(accountData).length > 0) {
          await updateDoc(accountRef, {
            userAutomations: accountAutomations,
            lastUpdated: serverTimestamp()
          });
        }
      } catch (accountErr) {
        console.error('Error updating account automation status:', accountErr);
      }
    }

    // Handle automationRequests collection for CRM tracking
    if (status === 'requested') {
      // Create a new request record
      try {
        const automationRequest = {
          userId: userId,
          automationId: automationId,
          name: automationData.name || automationData.title || 'Untitled Automation',
          description: automationData.description || 'No description available',
          softwares: automationData.softwares || [],
          category: automationData.category || 'general',
          status: 'requested',
          requestedBy: userData.displayName || userData.firstName + ' ' + userData.lastName || userData.email || 'Unknown User',
          company: company,
          createdAt: serverTimestamp(),
          workflowSteps: automationData.workflowSteps || []
        };
        
        await addDoc(collection(db, 'automationRequests'), automationRequest);
        
        // Trigger technology fulfillment notification
        try {
          const { triggerFulfillmentNotification } = await import('./notificationTriggerService');
          await triggerFulfillmentNotification('technology', {
            type: 'automation',
            name: automationRequest.name,
            description: automationRequest.description,
            requestedBy: automationRequest.requestedBy,
            company: automationRequest.company,
            category: automationRequest.category,
            softwares: automationRequest.softwares,
            timestamp: new Date().toISOString()
          });
          console.log('✅ Technology fulfillment notification sent for automation request');
        } catch (notificationError) {
          console.error('❌ Error sending technology fulfillment notification:', notificationError);
          // Don't fail the automation request if notification fails
        }
      } catch (error) {
        console.error('Error creating automation request record:', error);
        // Don't throw error here as the main user status update should still succeed
      }
    } else if (status === 'on') {
      // If changing to 'on', update existing request record to show it's on
      try {
        const requestsQuery = query(
          collection(db, 'automationRequests'),
          where('userId', '==', userId),
          where('automationId', '==', automationId)
        );
        
        const querySnapshot = await getDocs(requestsQuery);
        if (!querySnapshot.empty) {
          // Update the most recent request
          const requestDoc = querySnapshot.docs[0];
          await updateDoc(doc(db, 'automationRequests', requestDoc.id), {
            status: 'on',
            activatedAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        } else {
          // If no request exists, create one
          const automationRequest = {
            userId: userId,
            automationId: automationId,
            name: automationData.name || automationData.title || 'Untitled Automation',
            description: automationData.description || 'No description available',
            softwares: automationData.softwares || [],
            category: automationData.category || 'general',
            status: 'on',
            requestedBy: userData.displayName || userData.firstName + ' ' + userData.lastName || userData.email || 'Unknown User',
            company: company,
            createdAt: serverTimestamp(),
            activatedAt: serverTimestamp(),
            workflowSteps: automationData.workflowSteps || []
          };
          
          await addDoc(collection(db, 'automationRequests'), automationRequest);
        }
      } catch (error) {
        console.error('Error updating automation request records:', error);
        // Don't throw error here as the main user status update should still succeed
      }
    } else if (status === 'in-progress') {
      // If changing to 'in-progress', update existing request record to show it's in progress
      try {
        const requestsQuery = query(
          collection(db, 'automationRequests'),
          where('userId', '==', userId),
          where('automationId', '==', automationId)
        );
        
        const querySnapshot = await getDocs(requestsQuery);
        if (!querySnapshot.empty) {
          // Update the most recent request
          const requestDoc = querySnapshot.docs[0];
          await updateDoc(doc(db, 'automationRequests', requestDoc.id), {
            status: 'in-progress',
            inProgressAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        } else {
          // If no request exists, create one
          const automationRequest = {
            userId: userId,
            automationId: automationId,
            name: automationData.name || automationData.title || 'Untitled Automation',
            description: automationData.description || 'No description available',
            softwares: automationData.softwares || [],
            category: automationData.category || 'general',
            status: 'in-progress',
            requestedBy: userData.displayName || userData.firstName + ' ' + userData.lastName || userData.email || 'Unknown User',
            company: company,
            createdAt: serverTimestamp(),
            inProgressAt: serverTimestamp(),
            workflowSteps: automationData.workflowSteps || []
          };
          
          await addDoc(collection(db, 'automationRequests'), automationRequest);
        }
      } catch (error) {
        console.error('Error updating automation request records:', error);
        // Don't throw error here as the main user status update should still succeed
      }
    } else if (status === 'approved') {
      // Client approved: update existing request record so Technology Fulfillment shows it
      try {
        const requestsQuery = query(
          collection(db, 'automationRequests'),
          where('userId', '==', userId),
          where('automationId', '==', automationId)
        );
        const querySnapshot = await getDocs(requestsQuery);
        if (!querySnapshot.empty) {
          const requestDoc = querySnapshot.docs[0];
          await updateDoc(doc(db, 'automationRequests', requestDoc.id), {
            status: 'approved',
            lastUpdated: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error updating automation request records for approved:', error);
      }
    } else if (status === 'off') {
      // If changing to 'off', update existing request record to show it's off
      try {
        const requestsQuery = query(
          collection(db, 'automationRequests'),
          where('userId', '==', userId),
          where('automationId', '==', automationId)
        );
        
        const querySnapshot = await getDocs(requestsQuery);
        if (!querySnapshot.empty) {
          // Update the most recent request
          const requestDoc = querySnapshot.docs[0];
          await updateDoc(doc(db, 'automationRequests', requestDoc.id), {
            status: 'off',
            deactivatedAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error updating automation request records:', error);
        // Don't throw error here as the main user status update should still succeed
      }
    }
    
    return {
      success: true,
      message: 'Automation status saved successfully'
    };
  } catch (error) {
    console.error('Error saving user automation status:', error);
    throw error;
  }
};

/**
 * Save user's automation customizations
 * @param {string} userId - The user's UID
 * @param {string} automationId - The automation ID
 * @param {Object} customizations - The customizations object
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveUserAutomationCustomizations = async (userId, automationId, customizations, accountId = null) => {
  try {
    // Save on the user doc, preserving any existing status/fields.
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const userAutomations = userData.userAutomations || {};
      userAutomations[automationId] = {
        ...userAutomations[automationId],
        customizations,
        lastUpdated: serverTimestamp()
      };
      await updateDoc(userDocRef, {
        userAutomations,
        lastUpdated: serverTimestamp()
      });
    } else {
      await setDoc(userDocRef, {
        uid: userId,
        userAutomations: {
          [automationId]: { status: 'off', customizations, lastUpdated: serverTimestamp() }
        },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    }

    // Also persist company-wide customizations when an accountId is provided, so
    // every team member (and the CRM Portal View) reads the same customized
    // automation. getUserAutomationData prefers the account doc when given an id.
    if (accountId) {
      try {
        const accountRef = doc(db, 'accounts', accountId);
        const accountDoc = await getDoc(accountRef);
        const accountAutomations = (accountDoc.exists() ? accountDoc.data() : {}).userAutomations || {};
        accountAutomations[automationId] = {
          ...(accountAutomations[automationId] || {}),
          customizations,
          lastUpdated: serverTimestamp()
        };
        await setDoc(accountRef, {
          userAutomations: accountAutomations,
          lastUpdated: serverTimestamp()
        }, { merge: true });
      } catch (accountErr) {
        console.error('Error saving account automation customizations:', accountErr);
      }
    }

    return { success: true, message: 'Automation customizations saved successfully' };
  } catch (error) {
    console.error('Error saving user automation customizations:', error);
    throw error;
  }
};

/**
 * Update an existing user-created automation request (edit its name, emojis,
 * description, workflow steps, etc.) on the user doc and, when provided, the
 * company account doc so the change is company-wide.
 * @param {string} userId - The user's UID
 * @param {string} requestId - The automation request id
 * @param {Object} updates - Fields to merge into the request
 * @param {string|null} accountId - Optional account/company id
 */
export const updateUserAutomationRequest = async (userId, requestId, updates, accountId = null) => {
  try {
    const applyUpdate = async (ref) => {
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const requests = snap.data().userAutomationRequests || [];
      const next = requests.map((req) =>
        req.id === requestId ? { ...req, ...updates, lastUpdated: new Date().toISOString() } : req
      );
      await updateDoc(ref, { userAutomationRequests: next, lastUpdated: serverTimestamp() });
    };

    await applyUpdate(doc(db, 'users', userId));
    if (accountId) {
      try {
        await applyUpdate(doc(db, 'accounts', accountId));
      } catch (accountErr) {
        console.error('Error updating account automation request:', accountErr);
      }
    }
    return { success: true, message: 'Automation request updated successfully' };
  } catch (error) {
    console.error('Error updating user automation request:', error);
    throw error;
  }
};

/**
 * Get all user's automation data
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} All user's automation data
 */
export const getAllUserAutomationData = async (userId, accountId = null) => {
  try {
    // Merge the user doc's state with the company-wide account state, account
    // winning per automation. This mirrors getUserAutomationData's per-id
    // lookup (account entry preferred, user doc fallback) so list cards and
    // the detail popup always show the same status.
    let userAutomations = {};
    if (userId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        userAutomations = userDoc.data().userAutomations || {};
      }
    }

    if (accountId) {
      const accountDoc = await getDoc(doc(db, 'accounts', accountId));
      if (accountDoc.exists()) {
        const accountAutomations = accountDoc.data().userAutomations || {};
        return { ...userAutomations, ...accountAutomations };
      }
    }

    return userAutomations;
  } catch (error) {
    console.error('Error fetching all user automation data:', error);
    return {};
  }
};

/**
 * Save bug report for an automation
 * @param {string} userId - The user's UID
 * @param {string} automationId - The automation ID
 * @param {string} bugReport - The bug report text
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveBugReport = async (userId, automationId, bugReport) => {
  try {
    const bugReportsRef = collection(db, 'bugReports');
    
    await setDoc(doc(bugReportsRef), {
      userId,
      automationId,
      bugReport,
      status: 'open',
      createdAt: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });
    
    return {
      success: true,
      message: 'Bug report submitted successfully'
    };
  } catch (error) {
    console.error('Error saving bug report:', error);
    throw error;
  }
};

/**
 * Save user-specific automation request to user document
 * @param {string} userId - The user's UID
 * @param {Object} automationRequest - The automation request data
 * @param {Object} options - Optional parameters
 * @param {boolean} options.skipNotification - Skip sending notification email (default: false)
 * @param {string} options.accountId - Account ID to also save automation to account document
 * @param {string} options.status - Initial status (default: 'requested'; internal adds use 'in-progress')
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveUserAutomationRequest = async (userId, automationRequest, options = {}) => {
  try {
    const { skipNotification = false, accountId = null, status = 'requested' } = options;
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    // Generate a unique ID for the user's automation request
    const requestId = `user_request_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Use regular Date instead of serverTimestamp for array items
    const now = new Date();
    
    const userAutomationRequest = {
      id: requestId,
      ...automationRequest,
      userId: userId,
      status,
      createdAt: now.toISOString(),
      lastUpdated: now.toISOString()
    };
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const userAutomationRequests = userData.userAutomationRequests || [];
      
      // Add the new request to the array
      userAutomationRequests.push(userAutomationRequest);
      
      await updateDoc(userDocRef, {
        userAutomationRequests,
        lastUpdated: serverTimestamp()
      });
    } else {
      // Create user document if it doesn't exist
      await setDoc(userDocRef, {
        uid: userId,
        userAutomationRequests: [userAutomationRequest],
        userAutomations: {},
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    }
    
    // Also save to account document if accountId is provided
    if (accountId) {
      try {
        const accountRef = doc(db, 'accounts', accountId);
        const accountDoc = await getDoc(accountRef);
        
        if (accountDoc.exists()) {
          const accountData = accountDoc.data();
          const accountAutomationRequests = accountData.userAutomationRequests || [];
          
          // Add the new request to the account's array
          accountAutomationRequests.push(userAutomationRequest);
          
          await updateDoc(accountRef, {
            userAutomationRequests: accountAutomationRequests,
            lastUpdated: serverTimestamp()
          });
        }
      } catch (accountError) {
        console.error('Error saving automation to account:', accountError);
        // Don't fail the main operation if account save fails
      }
    }
    
    // Trigger technology fulfillment notification for user automation request (unless skipped)
    if (!skipNotification) {
      try {
        const { triggerFulfillmentNotification } = await import('./notificationTriggerService');
        await triggerFulfillmentNotification('technology', {
          type: 'automation',
          name: userAutomationRequest.name,
          description: userAutomationRequest.description,
          requestedBy: userAutomationRequest.requestedBy,
          company: userAutomationRequest.company,
          category: userAutomationRequest.category,
          softwares: userAutomationRequest.softwares,
          timestamp: userAutomationRequest.createdAt
        });
        console.log('✅ Technology fulfillment notification sent for user automation request');
      } catch (notificationError) {
        console.error('❌ Error sending technology fulfillment notification:', notificationError);
        // Don't fail the automation request if notification fails
      }
    }
    
    return {
      success: true,
      message: 'Automation request saved successfully',
      requestId: requestId
    };
  } catch (error) {
    console.error('Error saving user automation request:', error);
    throw error;
  }
};

/**
 * Get user's automation requests
 * @param {string} userId - The user's UID
 * @returns {Promise<Array>} Array of user's automation requests
 */
export const getUserAutomationRequests = async (userId, accountId = null) => {
  try {
    // Prefer company-wide requests when accountId is available
    if (accountId) {
      const accountRef = doc(db, 'accounts', accountId);
      const accountDoc = await getDoc(accountRef);
      if (accountDoc.exists()) {
        const accountData = accountDoc.data();
        const accountRequests = accountData.userAutomationRequests || [];
        if (accountRequests.length > 0) {
          return accountRequests;
        }
      }
    }

    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.userAutomationRequests || [];
    }

    return [];
  } catch (error) {
    console.error('Error fetching user automation requests:', error);
    return [];
  }
};

/**
 * Update automation status from CRM side (approve/deny requests)
 * @param {string} userId - The user's UID
 * @param {string} automationId - The automation template ID
 * @param {string} newStatus - The new status ('approved', 'denied', 'in-progress', 'active')
 * @returns {Promise<Object>} Result of the update operation
 */
export const updateAutomationStatusFromCRM = async (userId, automationId, newStatus) => {
  try {
    // Update the user's automation status directly
    if (newStatus === 'approved') {
      // When approved (by client or set from CRM), keep status as 'approved' for Technology Fulfillment
      await saveUserAutomationStatus(userId, automationId, 'approved');
    } else if (newStatus === 'denied') {
      // When denied, set user status to 'off'
      await saveUserAutomationStatus(userId, automationId, 'off');
    } else if (newStatus === 'in-progress') {
      // When in progress, set user status to 'in-progress'
      await saveUserAutomationStatus(userId, automationId, 'in-progress');
    } else if (newStatus === 'active') {
      // When active, set user status to 'on'
      await saveUserAutomationStatus(userId, automationId, 'on');
    }
    
    return {
      success: true,
      message: 'Automation status updated successfully'
    };
  } catch (error) {
    console.error('Error updating automation status from CRM:', error);
    throw error;
  }
};
